import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { TransportDistribution } from "@/types/transport-distribution/transport-distribution.types";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { transportDistributionService } from "@/services/transport-distribution/transportDistributionService";
import { useData } from "@/hooks/useData";
import { InformationSource } from "@/types/information-source/information-source.types";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface TransportDistributionFormProps {
  isOpen: boolean;
  onClose: () => void;
  distributionToEdit?: TransportDistribution | null;
  onSuccess: () => void;
}

export const TransportDistributionForm = ({
  isOpen,
  onClose,
  distributionToEdit,
  onSuccess,
}: TransportDistributionFormProps) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  // Form state
  const [vehicleType, setVehicleType] = useState("");
  const [fe_trans, setFe_trans] = useState<number | undefined>(undefined);
  const [fe_transSourceId, setFe_transSourceId] = useState("");
  const [fh_trans, setFh_trans] = useState<number | undefined>(undefined);
  const [fh_transSourceId, setFh_transSourceId] = useState("");

  // Validation state
  const [vehicleTypeError, setVehicleTypeError] = useState("");
  const [fe_transError, setFe_transError] = useState("");
  const [fe_transSourceError, setFe_transSourceError] = useState("");
  const [fh_transError, setFh_transError] = useState("");
  const [fh_transSourceError, setFh_transSourceError] = useState("");

  // Fetch information sources
  const { data: infoSources } = useData<InformationSource>({
    collectionName: "setup_infoSources",
  });

  // Initialize form when editing
  useEffect(() => {
    if (distributionToEdit) {
      setVehicleType(distributionToEdit.vehicleType || "");
      setFe_trans(distributionToEdit.fe_trans);
      setFe_transSourceId(distributionToEdit.fe_transSourceId || "");
      setFh_trans(distributionToEdit.fh_trans);
      setFh_transSourceId(distributionToEdit.fh_transSourceId || "");
    } else {
      // Reset form for new creation
      setVehicleType("");
      setFe_trans(undefined);
      setFe_transSourceId("");
      setFh_trans(undefined);
      setFh_transSourceId("");
    }
    // Reset validation errors when opening/closing the form
    setVehicleTypeError("");
    setFe_transError("");
    setFe_transSourceError("");
    setFh_transError("");
    setFh_transSourceError("");
  }, [distributionToEdit, isOpen]);

  const validateForm = () => {
    let isValid = true;

    // Validate vehicle type
    if (!vehicleType.trim()) {
      setVehicleTypeError(t("validation.required"));
      isValid = false;
    } else {
      setVehicleTypeError("");
    }

    // Validate fe_trans
    if (fe_trans === undefined || fe_trans === null) {
      setFe_transError(t("validation.required"));
      isValid = false;
    } else {
      setFe_transError("");
    }

    // Validate fe_trans source
    if (!fe_transSourceId) {
      setFe_transSourceError(t("validation.required"));
      isValid = false;
    } else {
      setFe_transSourceError("");
    }

    // Validate fh_trans
    if (fh_trans === undefined || fh_trans === null) {
      setFh_transError(t("validation.required"));
      isValid = false;
    } else {
      setFh_transError("");
    }

    // Validate fh_trans source
    if (!fh_transSourceId) {
      setFh_transSourceError(t("validation.required"));
      isValid = false;
    } else {
      setFh_transSourceError("");
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const distributionData: Partial<TransportDistribution> = {
        vehicleType: vehicleType.trim(),
        fe_trans: fe_trans || 0,
        fe_transSourceId,
        fh_trans: fh_trans || 0,
        fh_transSourceId,
      };

      if (distributionToEdit) {
        // Update existing distribution
        await transportDistributionService.edit(distributionToEdit.id, distributionData, user?.uid);
        toast({
          title: t("success"),
          description: t("transportDistribution.updated"),
        });
      } else {
        // Create new distribution
        await transportDistributionService.create(
          distributionData as Omit<TransportDistribution, "id" | "createdAt" | "updatedAt">,
          user?.uid
        );
        toast({
          title: t("success"),
          description: t("transportDistribution.created"),
        });
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error saving transport distribution:", error);

      // Handle specific API errors
      if (error.message === "DUPLICATE_VEHICLE_TYPE") {
        toast({
          title: t("error"),
          description: t("transportDistribution.errors.duplicateVehicleType"),
          variant: "destructive",
        });
      } else {
        // Generic error handling
        toast({
          title: t("error"),
          description: distributionToEdit
            ? t("transportDistribution.errors.updateError")
            : t("transportDistribution.errors.createError"),
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {distributionToEdit ? t("transportDistribution.editTitle") : t("transportDistribution.addTitle")}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="vehicleType">
                {t("transportDistribution.vehicleType")} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="vehicleType"
                type="text"
                value={vehicleType}
                onChange={(e) => setVehicleType(e.target.value)}
                onBlur={() => {
                  if (!vehicleType.trim()) {
                    setVehicleTypeError(t("validation.required"));
                  } else {
                    setVehicleTypeError("");
                  }
                }}
                placeholder={t("transportDistribution.vehicleTypePlaceholder")}
                className={`placeholder:text-gray-400 placeholder:text-sm ${vehicleTypeError ? "border-red-300" : ""}`}
                disabled={isLoading}
              />
              {vehicleTypeError && <p className="text-sm font-medium text-red-500">{vehicleTypeError}</p>}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="fe_trans">
                {t("transportDistribution.fe_trans")} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="fe_trans"
                type="number"
                step="any"
                value={fe_trans === undefined ? "" : fe_trans}
                onChange={(e) => setFe_trans(e.target.value === "" ? undefined : parseFloat(e.target.value))}
                onBlur={() => {
                  if (fe_trans === undefined || fe_trans === null) {
                    setFe_transError(t("validation.required"));
                  } else {
                    setFe_transError("");
                  }
                }}
                placeholder={t("transportDistribution.fe_transPlaceholder")}
                className={`placeholder:text-gray-400 placeholder:text-sm ${fe_transError ? "border-red-300" : ""}`}
                disabled={isLoading}
              />
              {fe_transError && <p className="text-sm font-medium text-red-500">{fe_transError}</p>}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="fe_transSource">
                {t("transportDistribution.fe_transSource")} <span className="text-red-500">*</span>
              </Label>
              <Select
                value={fe_transSourceId}
                onValueChange={(value) => {
                  setFe_transSourceId(value);
                  if (!value) {
                    setFe_transSourceError(t("validation.required"));
                  } else {
                    setFe_transSourceError("");
                  }
                }}
                disabled={isLoading}
              >
                <SelectTrigger
                  className={fe_transSourceError ? "border-red-300" : ""}
                  id="fe_transSource"
                >
                  <SelectValue placeholder={t("transportDistribution.fe_transSourcePlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {infoSources?.map((source) => (
                    <SelectItem key={source.id} value={source.id}>
                      {source.name} ({source.year})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fe_transSourceError && <p className="text-sm font-medium text-red-500">{fe_transSourceError}</p>}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="fh_trans">
                {t("transportDistribution.fh_trans")} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="fh_trans"
                type="number"
                step="any"
                value={fh_trans === undefined ? "" : fh_trans}
                onChange={(e) => setFh_trans(e.target.value === "" ? undefined : parseFloat(e.target.value))}
                onBlur={() => {
                  if (fh_trans === undefined || fh_trans === null) {
                    setFh_transError(t("validation.required"));
                  } else {
                    setFh_transError("");
                  }
                }}
                placeholder={t("transportDistribution.fh_transPlaceholder")}
                className={`placeholder:text-gray-400 placeholder:text-sm ${fh_transError ? "border-red-300" : ""}`}
                disabled={isLoading}
              />
              {fh_transError && <p className="text-sm font-medium text-red-500">{fh_transError}</p>}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="fh_transSource">
                {t("transportDistribution.fh_transSource")} <span className="text-red-500">*</span>
              </Label>
              <Select
                value={fh_transSourceId}
                onValueChange={(value) => {
                  setFh_transSourceId(value);
                  if (!value) {
                    setFh_transSourceError(t("validation.required"));
                  } else {
                    setFh_transSourceError("");
                  }
                }}
                disabled={isLoading}
              >
                <SelectTrigger
                  className={fh_transSourceError ? "border-red-300" : ""}
                  id="fh_transSource"
                >
                  <SelectValue placeholder={t("transportDistribution.fh_transSourcePlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {infoSources?.map((source) => (
                    <SelectItem key={source.id} value={source.id}>
                      {source.name} ({source.year})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fh_transSourceError && <p className="text-sm font-medium text-red-500">{fh_transSourceError}</p>}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              {t("cancel")}
            </Button>
            <Button type="submit" disabled={isLoading} className="bg-teal-600 hover:bg-teal-700 text-white">
              {isLoading ? t("saving") : distributionToEdit ? t("update") : t("save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}; 